import { z } from 'zod';

const money = () => z.number().finite().min(0);

const laborLine = z.object({ rate: money(), hours: money() });
const reimbLine = z.object({ rate: money(), qty: money() });

/** Spare rows the reimbursement section has in the template (rows 24-25). */
export const JOB_CARD_CUSTOM_ROW_CAPACITY = 2;

/** Column A of the job card is a fixed-width cell; longer names just spill. */
const LABEL_MAX_LENGTH = 40;

const customLine = z.object({
  label: z.string().max(LABEL_MAX_LENGTH),
  rate: money(),
  qty: money(),
});

export type JobCardCustomLine = z.infer<typeof customLine>;

/**
 * The four flat-cost rows (sheet rows 20-23) whose labels a job may override.
 * Toll and mileage are deliberately absent: they are the section's rate x qty
 * rows and keep the template's own names.
 */
export const JOB_CARD_DIRECT_COST_LABEL_KEYS = [
  'parking',
  'gasEstimate',
  'materials',
  'homeDepot',
] as const;

export type JobCardDirectCostLabelKey = (typeof JOB_CARD_DIRECT_COST_LABEL_KEYS)[number];

const customList = z.array(customLine).max(JOB_CARD_CUSTOM_ROW_CAPACITY).default([]);

export const jobCardFieldsSchema = z.object({
  labor: z.object({
    management: laborLine,
    office: laborLine,
    assistant: laborLine,
  }),
  reimbursement: z.object({
    toll: reimbLine,
    mileage: reimbLine,
    custom: customList,
  }),
  directCosts: z.object({
    parking: money(),
    gasEstimate: money(),
    materials: money(),
    homeDepot: money(),
  }),
  // Absent key = keep the template's own label. Present-but-empty = blank row.
  directCostLabels: z
    .object(
      Object.fromEntries(
        JOB_CARD_DIRECT_COST_LABEL_KEYS.map((k) => [k, z.string().max(LABEL_MAX_LENGTH)])
      ) as Record<JobCardDirectCostLabelKey, z.ZodString>
    )
    .partial()
    .default({}),
});

export type JobCardFields = z.infer<typeof jobCardFieldsSchema>;

export const JOB_CARD_FIELD_DEFAULTS: JobCardFields = {
  labor: {
    management: { rate: 83, hours: 1 },
    office: { rate: 35, hours: 0.5 },
    assistant: { rate: 50, hours: 0.5 },
  },
  reimbursement: {
    toll: { rate: 10.5, qty: 0 },
    mileage: { rate: 0.7, qty: 0 },
    custom: [],
  },
  directCosts: {
    parking: 0,
    gasEstimate: 0,
    materials: 0,
    homeDepot: 0,
  },
  directCostLabels: {},
};

function mergeCustom(stored: unknown): JobCardCustomLine[] {
  if (!Array.isArray(stored)) return [];
  return stored.slice(0, JOB_CARD_CUSTOM_ROW_CAPACITY).map((raw) => {
    const row = (raw ?? {}) as Partial<JobCardCustomLine>;
    // Number.isFinite, not typeof: money() is .finite(), so an Infinity here
    // would produce a value the schema itself would reject on the way back in.
    return {
      label: typeof row.label === 'string' ? row.label.slice(0, LABEL_MAX_LENGTH) : '',
      rate: Number.isFinite(row.rate) && (row.rate as number) >= 0 ? (row.rate as number) : 0,
      qty: Number.isFinite(row.qty) && (row.qty as number) >= 0 ? (row.qty as number) : 0,
    };
  });
}

function mergeDirectCostLabels(
  stored: unknown
): Partial<Record<JobCardDirectCostLabelKey, string>> {
  const src = (stored ?? {}) as Record<string, unknown>;
  const out: Partial<Record<JobCardDirectCostLabelKey, string>> = {};
  // Key-by-key, not a spread: an unknown key in old JSONB must not reach the
  // payload as a row the template doesn't have.
  for (const key of JOB_CARD_DIRECT_COST_LABEL_KEYS) {
    const value = src[key];
    if (typeof value === 'string') out[key] = value.slice(0, LABEL_MAX_LENGTH);
  }
  return out;
}

/**
 * Deep-merge a stored (possibly null or partial) value over the defaults so
 * every field is present. Used by the form (display) and the generator (write).
 */
export function mergeJobCardFields(
  stored: Partial<JobCardFields> | null | undefined
): JobCardFields {
  const d = JOB_CARD_FIELD_DEFAULTS;
  const s = stored ?? {};
  return {
    labor: {
      management: { ...d.labor.management, ...s.labor?.management },
      office: { ...d.labor.office, ...s.labor?.office },
      assistant: { ...d.labor.assistant, ...s.labor?.assistant },
    },
    reimbursement: {
      toll: { ...d.reimbursement.toll, ...s.reimbursement?.toll },
      mileage: { ...d.reimbursement.mileage, ...s.reimbursement?.mileage },
      custom: mergeCustom(s.reimbursement?.custom),
    },
    // Picked key-by-key rather than spread: jobs saved before the second GAS
    // row was dropped still carry a `gas` amount in their stored JSONB, and a
    // spread would carry it into the payload as a row the template no longer has.
    directCosts: {
      parking: s.directCosts?.parking ?? d.directCosts.parking,
      gasEstimate: s.directCosts?.gasEstimate ?? d.directCosts.gasEstimate,
      materials: s.directCosts?.materials ?? d.directCosts.materials,
      homeDepot: s.directCosts?.homeDepot ?? d.directCosts.homeDepot,
    },
    directCostLabels: mergeDirectCostLabels(s.directCostLabels),
  };
}
